"use client";

import { Hit } from "instantsearch.js";
import { SendEventForHits } from "instantsearch.js/es/lib/utils";
import NextImage, { getImageProps } from "next/image";
import { useEffect } from "react";

import { css } from "@/../styled-system/css";
import { EventType } from "@/actions/events/EventTypes/types";
import { sendUserEvents } from "@/actions/events/send-user-event-rpc";
import { useSearchStore } from "@/providers/search-store-provider";

import { Link } from "../ui/Link";

const productCardClass = css({
  display: "block",
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  height: "fit-content",
  rounded: "3xl",
  cursor: "pointer",
  overflow: "hidden",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease-in-out",
  _hover: {
    shadow: "md",
  },
});

const nameSpanClass = css({
  color: "brandBlack",
  wordBreak: "break-word",
  lineClamp: 2,
  textOverflow: "ellipsis",
  fontSize: "lg",
  sm: { fontSize: "1.375rem" },
  _before: {
    position: "absolute",
    content: '""',
    bottom: "0",
    left: "0",
    right: "0",
    top: "0",
  },
});

const priceSpanClass = css({
  wordBreak: "break-word",
  lineClamp: 1,
  textOverflow: "ellipsis",
  fontSize: "lg",
  fontWeight: "bold",
  color: "brandTeal",
  sm: { fontSize: "2xl" },
});

type SearchProductCardProps = {
  name: string;
  avg_price: number;
  image_url: string;
  src_set?: string;
  objectId: string;
  slug: string;
  sendEvent: SendEventForHits;
  hit: Hit;
};

export default function SearchProductCard({
  name,
  avg_price,
  image_url,
  slug,
  sendEvent,
  hit,
}: SearchProductCardProps) {
  const { clearQuery } = useSearchStore((state) => ({
    clearQuery: state.clearQuery,
  }));

  const onClick = () => {
    sendUserEvents(EventType.Click, [hit.objectID]);
    sendEvent("click", hit, "Hits Clicked");
    clearQuery();
  };

  const prefetchProps = getImageProps({
    height: 256,
    quality: 80,
    width: 256,
    // TODO: change placeholder
    src: image_url ?? "/placeholder.svg?height=64&width=64",
    alt: `A small picture of ${name}`,
  });

  useEffect(() => {
    try {
      const iprops = prefetchProps.props;
      const img = new Image();
      // Don't interfer with important requests
      (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority =
        "low";
      // Don't block the main thread with prefetch images
      img.decoding = "async";
      // Order is important here, sizes must be set before srcset, srcset must be set before src
      //   if (iprops.sizes) img.sizes = iprops.sizes;
      //   if (iprops.srcSet) img.srcset = iprops.srcSet;
      if (iprops.src) img.src = iprops.src;
    } catch (e) {
      console.error("failed to preload", prefetchProps.props.src, e);
    }
  }, [prefetchProps]);

  return (
    <div className={productCardClass}>
      <div
        className={css({
          position: "relative",
          backgroundColor: "gray.50",
        })}
      >
        <NextImage
          unoptimized
          loading="eager"
          decoding="sync"
          src={image_url}
          width={0}
          height={0}
          sizes="100vw"
          className={css({
            width: "100%",
            height: "auto",
            aspectRatio: "1",
            mixBlendMode: "multiply",
          })}
          alt={name}
        />
      </div>
      <div
        className={css({
          overflow: "hidden",
          p: "2",
          sm: { px: "5", pb: "5" },
        })}
      >
        <div
          className={css({
            mb: "1.5",
            height: "3.375rem",
            sm: { height: "4.125rem" },
          })}
        >
          <Link
            className={nameSpanClass}
            href={`/products/${slug}`}
            onClick={onClick}
            prefetch={true}
          >
            {name}
          </Link>
        </div>
        <div
          className={css({
            lineHeight: "none",
          })}
        >
          <span className={priceSpanClass}>${avg_price}+</span>
        </div>
      </div>
    </div>
  );
}
